import type { CSSProperties } from "react";
import { Menu, X } from "lucide-react";

/**
 * The hamburger menu button that toggles between the hamburger icon
 * (closed state) and the X icon (open state).
 *
 * Stateless: caller passes isOpen and onClick. The caller is
 * responsible for managing the drawer open/close state and keeping
 * isOpen in sync.
 *
 * Primarily used by AppLayout to toggle the mobile sidebar drawer.
 * Also usable in any header or toolbar that opens a drawer or menu.
 */

interface MobileHamburgerProps {
  isOpen: boolean;
  onClick: () => void;
  label?: string;
  size?: number;
}

const PALETTE = {
  ink: "#0E1A1A",
  bgFade: "#F3F5F5",
};

export function MobileHamburger({
  isOpen,
  onClick,
  label,
  size = 24,
}: MobileHamburgerProps) {
  const Icon = isOpen ? X : Menu;
  const ariaLabel = label ?? (isOpen ? "Close menu" : "Open menu");

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      onClick={onClick}
      style={buttonStyle}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          PALETTE.bgFade;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          "transparent";
      }}
    >
      <Icon size={size} color={PALETTE.ink} strokeWidth={2} />
    </button>
  );
}

const buttonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  padding: 0,
  border: 0,
  borderRadius: 8,
  background: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "background 120ms ease",
};